import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useHabitReminders } from "../lib/reminders";
import { Flame, Trophy, Calendar, Home, LogOut, ShieldCheck, Bell, BellOff, BookOpen } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const { enabled: remOn, permission: remPerm, toggle: toggleRem } = useHabitReminders(user?.username);

  const linkCls = ({ isActive }) =>
    `chip transition-colors ${isActive ? "bg-ink text-white" : "bg-white hover:bg-gold"}`;

  return (
    <div className="min-h-screen">
      <header className="border-b-2 border-ink bg-white sticky top-0 z-40" data-testid="app-header">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-link">
            <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-gold border-2 border-ink shadow-[3px_3px_0_0_#171717] flex items-center justify-center shrink-0">
              <Flame size={20} strokeWidth={2.5} />
            </div>
            <div className="font-display font-black text-base sm:text-xl leading-none truncate">
              Adi <span className="text-adi">vs</span> Kudrat
              <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-neutral-500 mt-1">30-day challenge</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-3">
            <NavLink to="/" className={linkCls} end data-testid="nav-dashboard"><Home size={14} /> Dashboard</NavLink>
            <NavLink to="/history" className={linkCls} data-testid="nav-history"><Calendar size={14} /> Calendar</NavLink>
            <NavLink to="/awards" className={linkCls} data-testid="nav-awards"><Trophy size={14} /> Awards</NavLink>
            <NavLink to="/bonuses" className={linkCls} data-testid="nav-bonuses"><BookOpen size={14} /> Rules</NavLink>
            {user?.role === "admin" && (
              <NavLink to="/admin" className={linkCls} data-testid="nav-admin"><ShieldCheck size={14} /> Admin</NavLink>
            )}
          </nav>

          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            {user?.role !== "admin" && (
              <button
                onClick={toggleRem}
                className={`nb-btn px-2 sm:px-3 py-2 text-xs flex items-center gap-2 ${remOn ? "bg-emerald-300" : "bg-white"}`}
                title={remPerm === "denied" ? "Notifications blocked in browser" : (remOn ? "Reminders on" : "Enable reminders")}
                data-testid="toggle-reminders"
              >
                {remOn ? <Bell size={14} /> : <BellOff size={14} />}
                <span className="hidden sm:inline">{remOn ? "On" : "Remind"}</span>
              </button>
            )}
            {user && (
              <div className="hidden sm:flex items-center gap-2 chip px-1 py-1 pr-3" data-testid="user-chip" style={{ background: user.color, color: "#fff", borderColor: "#171717" }}>
                {user.avatar_url && (
                  <img src={user.avatar_url} alt={user.display_name}
                       className="w-8 h-8 rounded-full border-2 border-white object-cover" />
                )}
                <span>{user.display_name}</span>
              </div>
            )}
            <button onClick={() => { logout(); nav("/login"); }} className="nb-btn bg-white px-2 sm:px-3 py-2 text-xs sm:text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut size={14} /> <span className="hidden xs:inline sm:inline">Logout</span>
            </button>
          </div>
        </div>
        <div className="md:hidden flex gap-2 px-3 pb-3 overflow-x-auto no-scrollbar">
          <NavLink to="/" end className={linkCls} data-testid="mnav-dashboard"><Home size={12} /> Dashboard</NavLink>
          <NavLink to="/history" className={linkCls} data-testid="mnav-history"><Calendar size={12} /> Calendar</NavLink>
          <NavLink to="/awards" className={linkCls} data-testid="mnav-awards"><Trophy size={12} /> Awards</NavLink>
          <NavLink to="/bonuses" className={linkCls} data-testid="mnav-bonuses"><BookOpen size={12} /> Rules</NavLink>
          {user?.role === "admin" && (
            <NavLink to="/admin" className={linkCls} data-testid="mnav-admin"><ShieldCheck size={12} /> Admin</NavLink>
          )}
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">{children}</main>
    </div>
  );
}
